/**
 * Authenticated WebSocket hook for Django Channels endpoints.
 *
 * Channels endpoints (e.g. ``/ws/orders/<id>/chat/``, ``/ws/panel/inbox/``) are
 * authenticated by a ``TelegramInitDataMiddleware`` that validates the same raw
 * ``initData`` HMAC as the REST layer. Browsers cannot set custom headers on a
 * WebSocket handshake, so the raw initData is carried in the ``Sec-WebSocket-Protocol``
 * subprotocol list — ``["tma", "<raw initData>"]`` — which the middleware reads.
 * This keeps the signed payload out of the URL (and thus out of access logs).
 *
 * The hook manages connection lifecycle with bounded exponential-backoff
 * reconnection and exposes a typed ``send`` plus parsed JSON messages.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { getRawInitData } from "./initData";

export type WebSocketStatus =
  | "connecting"
  | "open"
  | "closed"
  | "error";

export interface UseWebSocketOptions<TIncoming> {
  /** Parse a raw text frame into a typed message. Defaults to ``JSON.parse``. */
  readonly parse?: (raw: string) => TIncoming;
  /** Called for every successfully parsed incoming message. */
  readonly onMessage?: (message: TIncoming) => void;
  /** Disable auto-(re)connect (e.g. before an id is known). Defaults to ``true``. */
  readonly enabled?: boolean;
  /** Max reconnect attempts before giving up. Defaults to ``Infinity``. */
  readonly maxRetries?: number;
}

export interface UseWebSocketResult<TIncoming, TOutgoing> {
  readonly status: WebSocketStatus;
  readonly lastMessage: TIncoming | null;
  /** Send a JSON-serializable payload; returns ``false`` if the socket is not open. */
  readonly send: (payload: TOutgoing) => boolean;
}

const BASE_BACKOFF_MS = 500;
const MAX_BACKOFF_MS = 15_000;

/**
 * Resolve a path or absolute URL into a concrete ``ws(s)://`` URL. Exported for
 * testing; ``ws://`` / ``wss://`` inputs are returned unchanged.
 */
export function resolveWebSocketUrl(url: string): string {
  if (url.startsWith("ws://") || url.startsWith("wss://")) {
    return url;
  }
  const scheme =
    typeof window !== "undefined" && window.location.protocol === "https:"
      ? "wss"
      : "ws";
  const host =
    typeof window !== "undefined" ? window.location.host : "localhost";
  const path = url.startsWith("/") ? url : `/${url}`;
  return `${scheme}://${host}${path}`;
}

/**
 * Append the Telegram ``initData`` to a WS URL as the ``tgWebAppData`` query param.
 *
 * Raw initData (``query_id=...&user=...&hash=...``) contains ``= & % { }`` which are
 * INVALID in a ``Sec-WebSocket-Protocol`` token — passing it as a subprotocol makes the
 * ``WebSocket`` constructor throw. So we carry it in the query string instead, which the
 * Channels middleware already reads (``?tgWebAppData=``). The value is percent-encoded so
 * the server recovers the exact original string for HMAC validation. With no initData the
 * URL is returned unchanged (the server then rejects the unauthenticated socket).
 *
 * Exported for testing.
 */
export function buildAuthedWsUrl(
  baseUrl: string,
  rawInitData: string | null,
): string {
  if (rawInitData === null || rawInitData.length === 0) {
    return baseUrl;
  }
  const sep = baseUrl.includes("?") ? "&" : "?";
  return `${baseUrl}${sep}tgWebAppData=${encodeURIComponent(rawInitData)}`;
}

/**
 * Connect to an authenticated Channels WebSocket endpoint.
 *
 * @typeParam TIncoming - Shape of parsed messages received from the server.
 * @typeParam TOutgoing - Shape of payloads sent to the server.
 * @param url - Absolute ``ws(s)://`` URL or a path like ``/ws/orders/1/chat/``.
 */
export function useWebSocket<TIncoming = unknown, TOutgoing = unknown>(
  url: string,
  options: UseWebSocketOptions<TIncoming> = {},
): UseWebSocketResult<TIncoming, TOutgoing> {
  const {
    parse = (raw: string) => JSON.parse(raw) as TIncoming,
    onMessage,
    enabled = true,
    maxRetries = Number.POSITIVE_INFINITY,
  } = options;

  const [status, setStatus] = useState<WebSocketStatus>("closed");
  const [lastMessage, setLastMessage] = useState<TIncoming | null>(null);

  const socketRef = useRef<WebSocket | null>(null);
  const retriesRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closedByUserRef = useRef(false);

  // Keep latest callbacks without re-triggering the connect effect.
  const parseRef = useRef(parse);
  const onMessageRef = useRef(onMessage);
  parseRef.current = parse;
  onMessageRef.current = onMessage;

  useEffect(() => {
    if (!enabled) {
      return;
    }

    closedByUserRef.current = false;
    const target = resolveWebSocketUrl(url);

    const connect = (): void => {
      setStatus("connecting");

      const ws = new WebSocket(buildAuthedWsUrl(target, getRawInitData()));
      socketRef.current = ws;

      ws.onopen = () => {
        retriesRef.current = 0;
        setStatus("open");
      };

      ws.onmessage = (event: MessageEvent<string>) => {
        try {
          const message = parseRef.current(event.data);
          setLastMessage(message);
          onMessageRef.current?.(message);
        } catch {
          // Ignore malformed frames; the server is the source of truth.
        }
      };

      ws.onerror = () => {
        setStatus("error");
      };

      ws.onclose = () => {
        socketRef.current = null;
        setStatus("closed");
        if (closedByUserRef.current || retriesRef.current >= maxRetries) {
          return;
        }
        const attempt = retriesRef.current++;
        const delay = Math.min(
          BASE_BACKOFF_MS * 2 ** attempt,
          MAX_BACKOFF_MS,
        );
        timerRef.current = setTimeout(connect, delay);
      };
    };

    connect();

    return () => {
      closedByUserRef.current = true;
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      socketRef.current?.close();
      socketRef.current = null;
    };
  }, [url, enabled, maxRetries]);

  const send = useCallback((payload: TOutgoing): boolean => {
    const ws = socketRef.current;
    if (ws === null || ws.readyState !== WebSocket.OPEN) {
      return false;
    }
    ws.send(JSON.stringify(payload));
    return true;
  }, []);

  return { status, lastMessage, send };
}
